"use client";

import React, { useState } from "react";
import {
  Star,
  MapPin,
  Clock,
  Users,
  Calendar,
  ChevronLeft,
  ChevronRight,
  Maximize2,
  Share2,
  ExternalLink,
  Check,
} from "lucide-react";
import SEO from "../../components/SEO/SEO";

// Reusable Badge Component
function Badge({ children, variant = "default" }) {
  const variants = {
    default: "bg-white text-gray-900 px-3 py-1 rounded-full text-sm font-medium",
    dark: "bg-gray-900 text-white px-3 py-1 rounded-full text-sm font-medium",
  };

  return <span className={variants[variant]}>{children}</span>;
}

// Reusable Button Component
function Button({ children, variant = "primary", size = "md", fullWidth = false, onClick, disabled = false }) {
  const variants = {
    primary: "bg-blue-600 text-white hover:bg-blue-700",
    secondary: "bg-white text-gray-900 border border-gray-300 hover:bg-gray-50",
    ghost: "bg-transparent text-gray-700 hover:bg-gray-100",
  };

  const sizes = {
    sm: "px-3 py-1.5 text-sm",
    md: "px-4 py-2 text-base",
    lg: "px-6 py-3 text-lg",
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`${variants[variant]} ${sizes[size]} ${fullWidth ? "w-full" : ""} rounded-lg font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed`}
    >
      {children}
    </button>
  );
}

// Reusable Rating Display Component
function RatingDisplay({ rating, reviewCount }) {
  return (
    <div className="flex items-center gap-2">
      <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
      <span className="font-semibold">{rating}</span>
      <span className="text-gray-600">({reviewCount} reviews)</span>
    </div>
  );
}

// Reusable Info Item Component
function InfoItem({ icon: Icon, title, subtitle }) {
  return (
    <div className="flex items-start gap-3 p-4 bg-gray-50 rounded-lg">
      <Icon className="w-5 h-5 text-gray-600 mt-0.5" />
      <div>
        <div className="font-medium text-gray-900">{title}</div>
        {subtitle && <div className="text-sm text-gray-600">{subtitle}</div>}
      </div>
    </div>
  );
}

// Reusable Review Card Component
function ReviewCard({ name, date, rating, text, avatar, helpfulCount }) {
  return (
    <div className="border border-gray-200 rounded-lg p-6">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <img src={avatar || "/placeholder.svg"} alt={name} className="w-12 h-12 rounded-full object-cover" />
          <div>
            <div className="font-semibold text-gray-900">{name}</div>
            <div className="text-sm text-gray-600">{date}</div>
          </div>
        </div>
        <button className="text-gray-400 hover:text-gray-600">
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
          </svg>
        </button>
      </div>

      <div className="flex gap-1 mb-3">
        {[...Array(5)].map((_, i) => (
          <Star key={i} className={`w-4 h-4 ${i < rating ? "fill-yellow-400 text-yellow-400" : "text-gray-300"}`} />
        ))}
      </div>

      <p className="text-gray-700 mb-4 leading-relaxed">{text}</p>

      <div className="flex items-center gap-4">
        <button className="text-sm text-gray-600 hover:text-gray-900">Helpful ({helpfulCount})</button>
        <button className="text-sm text-gray-600 hover:text-gray-900">Reply</button>
      </div>
    </div>
  );
}

// Reusable Experience Card Component (for Similar Experiences)
function ExperienceCard({ title, location, rating, reviewCount, duration, price, image, category }) {
  return (
    <div className="bg-white rounded-lg overflow-hidden border border-gray-200 hover:shadow-lg transition-shadow">
      <div className="relative h-48">
        <img src={image || "/placeholder.svg"} alt={title} className="w-full h-full object-cover" />
        <div className="absolute top-3 left-3">
          <Badge variant="default">{category}</Badge>
        </div>
      </div>
      <div className="p-4">
        <h3 className="font-semibold text-gray-900 mb-2 line-clamp-2">{title}</h3>
        <div className="flex items-center gap-1 text-sm text-gray-600 mb-3">
          <MapPin className="w-4 h-4" />
          <span>{location}</span>
        </div>
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-1">
            <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" />
            <span className="font-medium">{rating}</span>
            <span className="text-gray-600">({reviewCount})</span>
          </div>
          <span className="text-sm text-gray-600">{duration}</span>
        </div>
        <div className="flex items-center justify-between">
          <div>
            <span className="text-xl font-bold text-gray-900">${(price || 0).toLocaleString()}</span>
            <span className="text-gray-600"> /person</span>
          </div>
          <Button variant="primary" size="sm" onClick={() => {}}>
            View Details
          </Button>
        </div>
      </div>
    </div>
  );
}

// Main Experience Detail Component
export default function ExperienceDetail() {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [activeTab, setActiveTab] = useState("description");
  const [participants, setParticipants] = useState(1);

  const images = [
    "https://hebbkx1anhila5yf.public.blob.vercel-storage.com/Frame%201000003405.png-4ssyJxSMvqEkSr2xGLaXSktKbxrs13.jpeg",
    "https://hebbkx1anhila5yf.public.blob.vercel-storage.com/attachments/gen-images/public/modern-coworking-space-with-desks-e7GqTaNU9BPDXPIffGB4qkOfsJZBye.jpg",
    "https://hebbkx1anhila5yf.public.blob.vercel-storage.com/attachments/gen-images/public/creative-studio-workspace-bright-xQKr607vvGJYK20EGGYz0kGjUEpLDC.jpg",
    "https://hebbkx1anhila5yf.public.blob.vercel-storage.com/attachments/gen-images/public/executive-meeting-room-table-GKFjrnACucjtIrgzlZZeXPTMoGgrVz.jpg",
  ];

  const reviews = [
    {
      name: "Michael Chen",
      date: "August 2024",
      rating: 5,
      avatar: "https://i.pravatar.cc/150?img=12",
      text: "Had an incredible experience! Sarah was an amazing guide who knew every trail and shared fascinating stories about the local wildlife. The views were breathtaking and the hiking was challenging but manageable. Highly recommend this for anyone looking for an authentic mountain adventure.",
      helpfulCount: 12,
    },
    {
      name: "Emily Rodriguez",
      date: "July 2024",
      rating: 5,
      avatar: "https://i.pravatar.cc/150?img=45",
      text: "Perfect day out in nature! The equipment provided was top-notch and Sarah made sure everyone in our group felt comfortable and safe. The lunch spot she had the most amazing view I've ever seen. Already planning to come back!",
      helpfulCount: 8,
    },
    {
      name: "David Thompson",
      date: "June 2024",
      rating: 4,
      avatar: "https://i.pravatar.cc/150?img=33",
      text: "Great hiking experience with stunning scenery. The trail was well-chosen and the pace was perfect for our mixed-ability group. Only minor issue was that it started to rain towards the end, but that's nature for you!",
      helpfulCount: 5,
    },
  ];

  const ratingBreakdown = [
    { stars: 5, count: 72 },
    { stars: 4, count: 18 },
    { stars: 3, count: 3 },
    { stars: 2, count: 1 },
    { stars: 1, count: 0 },
  ];

  const similarExperiences = [
    {
      title: "Sunrise Photography Workshop in the Mountains",
      location: "Rocky Mountains",
      rating: 4.9,
      reviewCount: 45,
      duration: "4 hours",
      price: 85,
      image:
        "https://hebbkx1anhila5yf.public.blob.vercel-storage.com/attachments/gen-images/public/modern-coworking-space-with-desks-e7GqTaNU9BPDXPIffGB4qkOfsJZBye.jpg",
      category: "Photography",
    },
    {
      title: "Multi-Day Wilderness Camping Adventure",
      location: "Colorado Forest",
      rating: 4.7,
      reviewCount: 23,
      duration: "3 days",
      price: 350,
      image:
        "https://hebbkx1anhila5yf.public.blob.vercel-storage.com/attachments/gen-images/public/creative-studio-workspace-bright-xQKr607vvGJYK20EGGYz0kGjUEpLDC.jpg",
      category: "Camping",
    },
    {
      title: "Rock Climbing for Beginners",
      location: "Boulder Canyon",
      rating: 4.8,
      reviewCount: 67,
      duration: "5 hours",
      price: 140,
      image:
        "https://hebbkx1anhila5yf.public.blob.vercel-storage.com/attachments/gen-images/public/executive-meeting-room-table-GKFjrnACucjtIrgzlZZeXPTMoGgrVz.jpg",
      category: "Climbing",
    },
  ];

  const nextImage = () => {
    setCurrentImageIndex(prev => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setCurrentImageIndex(prev => (prev - 1 + images.length) % images.length);
  };

  const pricePerPerson = 120;
  const serviceFee = 25;
  const total = pricePerPerson * participants + serviceFee;

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Experience Details | Spacia - Book Unique Experiences"
        description="Discover and book unique experiences, events, and activities across Africa. From guided tours to workshops, find your next adventure on Spacia."
        keywords={[
          "spacia experiences",
          "book experiences",
          "africa experiences",
          "events booking",
          "activities africa",
        ]}
        url={typeof window !== "undefined" ? window.location.href : ""}
        type="article"
      />
      {/* Header */}
      <header className="border-b border-gray-200 bg-white sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="text-xl font-semibold text-gray-900">Experience Detail</div>
          <div className="flex items-center gap-4">
            <button className="text-gray-700 hover:text-gray-900 font-medium">Book Experience</button>
            <button className="text-gray-700 hover:text-gray-900 font-medium">My Dashboard</button>
          </div>
        </div>
      </header>

      {/* Hero Image Gallery */}
      <div className="relative h-[500px] bg-gray-900">
        <img
          src={images[currentImageIndex] || "/placeholder.svg"}
          alt="Experience"
          className="w-full h-full object-cover"
        />

        {/* Navigation Arrows */}
        <button
          onClick={prevImage}
          className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white rounded-full flex items-center justify-center hover:bg-gray-100 transition-colors"
        >
          <ChevronLeft className="w-6 h-6 text-gray-900" />
        </button>
        <button
          onClick={nextImage}
          className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-white rounded-full flex items-center justify-center hover:bg-gray-100 transition-colors"
        >
          <ChevronRight className="w-6 h-6 text-gray-900" />
        </button>

        {/* Thumbnail Navigation */}
        <div className="absolute bottom-4 left-4 flex gap-2">
          {images.map((img, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentImageIndex(idx)}
              className={`w-16 h-16 rounded-lg overflow-hidden border-2 transition-all ${
                idx === currentImageIndex ? "border-white scale-110" : "border-transparent opacity-70 hover:opacity-100"
              }`}
            >
              <img
                src={img || "/placeholder.svg"}
                alt={`Thumbnail ${idx + 1}`}
                className="w-full h-full object-cover"
              />
            </button>
          ))}
        </div>

        {/* Image Counter & Fullscreen */}
        <div className="absolute bottom-4 right-4 flex items-center gap-3">
          <div className="bg-black/50 text-white px-3 py-1 rounded-full text-sm">
            {currentImageIndex + 1}/{images.length}
          </div>
          <button className="w-10 h-10 bg-white rounded-full flex items-center justify-center hover:bg-gray-100 transition-colors">
            <Maximize2 className="w-5 h-5 text-gray-900" />
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Left Column - Main Content */}
          <div className="lg:col-span-2">
            {/* Title & Tags */}
            <h1 className="text-4xl font-bold text-gray-900 mb-4">Epic Mountain Hiking Adventure</h1>
            <div className="flex flex-wrap gap-2 mb-4">
              <Badge>Adventure</Badge>
              <Badge>Hiking</Badge>
              <Badge>Nature</Badge>
              <Badge>Outdoor</Badge>
            </div>

            <RatingDisplay rating={4.8} reviewCount={94} />

            {/* Host Info */}
            <div className="mt-6 p-4 border border-gray-200 rounded-lg flex items-center gap-4">
              <div className="w-12 h-12 bg-gray-300 rounded-full flex items-center justify-center text-gray-600 font-semibold text-lg">
                S
              </div>
              <div>
                <div className="font-semibold text-gray-900">Hosted by Sarah Johnson</div>
                <div className="flex items-center gap-2 text-sm">
                  <Star className="w-3 h-3 fill-yellow-400 text-yellow-400" />
                  <span className="font-medium">4.8</span>
                  <span className="text-gray-600">· 187 reviews</span>
                </div>
              </div>
            </div>

            {/* Info Grid */}
            <div className="mt-6 grid grid-cols-2 gap-4">
              <InfoItem icon={Calendar} title="September 15, 2024" />
              <InfoItem icon={Clock} title="8:00 AM" subtitle="6 hours" />
              <InfoItem icon={MapPin} title="Rocky Mountain National Park, Colorado" subtitle="Meeting point" />
              <InfoItem icon={Users} title="2-12 people" subtitle="Group size" />
            </div>

            {/* Tabs */}
            <div className="mt-8 border-b border-gray-200">
              <div className="flex gap-8">
                {["description", "itinerary", "included"].map(tab => (
                  <button
                    key={tab}
                    onClick={() => setActiveTab(tab)}
                    className={`pb-4 font-medium capitalize transition-colors ${
                      activeTab === tab
                        ? "text-gray-900 border-b-2 border-gray-900"
                        : "text-gray-600 hover:text-gray-900"
                    }`}
                  >
                    {tab === "included" ? "What's Included" : tab}
                  </button>
                ))}
              </div>
            </div>

            {/* Tab Content */}
            <div className="mt-6 space-y-4 text-gray-700 leading-relaxed">
              <p>
                Embark on an unforgettable journey through the breathtaking landscapes of Rocky Mountain National Park.
                This full-day hiking adventure takes you through diverse terrains, from alpine meadows to rugged
                mountain peaks, offering spectacular views and wildlife viewing opportunities.
              </p>
              <p>
                Led by experienced local guides, you'll discover hidden trails and learn about the unique ecosystem of
                the Rocky Mountains. Whether you're an experienced hiker or a nature enthusiast looking for a challenge,
                this adventure is designed to accommodate various fitness levels while ensuring everyone experiences the
                magic of the mountains.
              </p>
              <p>
                Our small group sizes ensure personalized attention and a more intimate connection with nature. You'll
                have plenty of opportunities for photography, wildlife spotting, and simply soaking in the peaceful
                mountain atmosphere.
              </p>
            </div>

            {/* Reviews Section */}
            <div className="mt-12">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Reviews & Testimonials</h2>

              {/* Rating Summary */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-8">
                <div className="text-center">
                  <div className="text-5xl font-bold text-gray-900 mb-2">4.8</div>
                  <div className="flex justify-center gap-1 mb-2">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <div className="text-gray-600">Based on 94 reviews</div>
                </div>

                <div className="space-y-2">
                  {ratingBreakdown.map(item => (
                    <div key={item.stars} className="flex items-center gap-3">
                      <span className="text-sm font-medium text-gray-700 w-8">{item.stars}★</span>
                      <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-yellow-400 rounded-full"
                          style={{ width: `${(item.count / 94) * 100}%` }}
                        />
                      </div>
                      <span className="text-sm text-gray-600 w-8 text-right">{item.count}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Review Cards */}
              <div className="space-y-4">
                {reviews.map((review, idx) => (
                  <ReviewCard key={idx} {...review} />
                ))}
              </div>

              <div className="mt-6 text-center">
                <Button variant="secondary" onClick={() => {}}>
                  Show More Reviews
                </Button>
              </div>
            </div>

            {/* Similar Experiences */}
            <div className="mt-12">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Similar Experiences</h2>
                <div className="flex gap-2">
                  <button className="w-10 h-10 border border-gray-300 rounded-full flex items-center justify-center hover:bg-gray-50">
                    <ChevronLeft className="w-5 h-5" />
                  </button>
                  <button className="w-10 h-10 border border-gray-300 rounded-full flex items-center justify-center hover:bg-gray-50">
                    <ChevronRight className="w-5 h-5" />
                  </button>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {similarExperiences.map((exp, idx) => (
                  <ExperienceCard key={idx} {...exp} />
                ))}
              </div>
            </div>
          </div>

          {/* Right Column - Booking Sidebar */}
          <div className="lg:col-span-1">
            <div className="sticky top-24 border border-gray-200 rounded-lg p-6 bg-white shadow-lg">
              <div className="mb-6">
                <div className="text-sm text-gray-600 mb-1">Starting from</div>
                <div className="text-3xl font-bold text-gray-900">
                  ${(pricePerPerson || 0).toLocaleString()}
                  <span className="text-lg font-normal text-gray-600"> per person</span>
                </div>
              </div>

              {/* Date Selection */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-900 mb-2">Select Date</label>
                <button className="w-full px-4 py-3 border border-gray-300 rounded-lg text-left flex items-center justify-between hover:border-gray-400 transition-colors">
                  <span className="text-gray-600">Choose date</span>
                  <Calendar className="w-5 h-5 text-gray-400" />
                </button>
              </div>

              {/* Experience Type */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-900 mb-2">Experience Type</label>
                <select className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-white">
                  <option>Standard Experience - ${(pricePerPerson || 0).toLocaleString()}</option>
                  <option>Premium Experience - $180</option>
                </select>
              </div>

              {/* Number of Participants */}
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-900 mb-2">Number of Participants</label>
                <div className="flex items-center justify-between border border-gray-300 rounded-lg px-4 py-3">
                  <button
                    onClick={() => setParticipants(Math.max(1, participants - 1))}
                    className="w-8 h-8 flex items-center justify-center text-gray-600 hover:bg-gray-100 rounded"
                  >
                    -
                  </button>
                  <div className="flex items-center gap-2">
                    <Users className="w-5 h-5 text-gray-400" />
                    <span className="font-medium">
                      {participants} person{participants > 1 ? "s" : ""}
                    </span>
                  </div>
                  <button
                    onClick={() => setParticipants(Math.min(12, participants + 1))}
                    className="w-8 h-8 flex items-center justify-center text-gray-600 hover:bg-gray-100 rounded"
                  >
                    +
                  </button>
                </div>
              </div>

              {/* Price Breakdown */}
              <div className="mb-6 space-y-3 pb-6 border-b border-gray-200">
                <div className="flex justify-between text-gray-700">
                  <span>Standard Experience x {participants}</span>
                  <span>${(pricePerPerson * participants || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-gray-700">
                  <span>Service fee</span>
                  <span>${(serviceFee || 0).toLocaleString()}</span>
                </div>
                <div className="flex justify-between text-lg font-bold text-gray-900">
                  <span>Total</span>
                  <span>${(total || 0).toLocaleString()}</span>
                </div>
              </div>

              {/* Book Button */}
              <Button variant="primary" size="lg" fullWidth onClick={() => {}}>
                Book Now - ${(total || 0).toLocaleString()}
              </Button>

              <Button variant="secondary" size="md" fullWidth onClick={() => {}}>
                <Share2 className="w-4 h-4 mr-2 inline" />
                Share This Experience
              </Button>

              {/* Policies */}
              <div className="mt-6 space-y-2 text-sm text-gray-600">
                <div className="flex items-start gap-2">
                  <Clock className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>Free cancellation up to 24 hours before</span>
                </div>
                <div className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>Meeting point details will be shared after booking</span>
                </div>
              </div>

              {/* Location Section */}
              <div className="mt-6 pt-6 border-t border-gray-200">
                <h3 className="font-semibold text-gray-900 mb-4">Location</h3>

                {/* Map Preview */}
                <div className="relative h-48 bg-green-50 rounded-lg mb-4 overflow-hidden">
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center">
                      <MapPin className="w-12 h-12 text-green-600 mx-auto mb-2" />
                      <div className="font-medium text-gray-900">Interactive Map</div>
                    </div>
                  </div>
                </div>

                <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-3">
                  <div className="font-medium text-gray-900">Rocky Mountain National Park, Colorado</div>
                  <div className="text-sm text-gray-600">Meeting point</div>
                </div>

                <div className="space-y-2">
                  <button className="w-full flex items-center gap-2 text-gray-700 hover:text-gray-900 text-sm">
                    <Check className="w-4 h-4" />
                    <span>Get Directions</span>
                  </button>
                  <button className="w-full flex items-center gap-2 text-gray-700 hover:text-gray-900 text-sm">
                    <ExternalLink className="w-4 h-4" />
                    <span>View in Google Maps</span>
                  </button>
                </div>

                <div className="mt-4 space-y-2 text-sm text-gray-600">
                  <div className="flex items-start gap-2">
                    <Check className="w-4 h-4 mt-0.5 flex-shrink-0 text-green-600" />
                    <span>Exact meeting point will be shared after booking</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <Check className="w-4 h-4 mt-0.5 flex-shrink-0 text-green-600" />
                    <span>Free parking available nearby</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
